import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, IconButton, LinearProgress, MenuItem, Paper, Select,
    TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSnackbar } from 'notistack';
import getCookies from '../../utils/getCookies';
import { formatPhone, personName } from '../../voice/callFormat';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import UploadGate from './UploadGate';
import {
    WHO_LABEL, candidateNotes, dayName, dayPhrase, dayUploadHint, formatListDate,
} from './missedCallFormat';
import useScreenshotImport from './useScreenshotImport';
import DuplicateChoice from './DuplicateChoice';
import DayChoice from './DayChoice';

// iOS Safari zooms the page when a field under 16px takes focus; the dashboard theme's base is smaller.
const INPUT_SX = { '& .MuiInputBase-input': { fontSize: 16 } };
const BIG_BUTTON_SX = { flex: 1, minWidth: 0, py: 1.4, fontSize: 16, textTransform: 'none', whiteSpace: 'nowrap' };

const whoFromCookie = () => {
    const raw = getCookies('_auth_state');
    if (!raw) return null;
    try { return JSON.parse(raw).who || null; } catch { return (/"who"\s*:\s*"([^"]+)"/.exec(raw) || [])[1] || null; }
};

/** One candidate as a card — the phone's stand-in for a row of the desktop review table. */
const ReviewCard = ({ row, error, onChange, pastDay = false, days = [] }) => {
    // Several days in one upload (plan §3.9): the reviewer has to see which list each row goes on — and be able to
    // say, for a row typed in by hand or a 다른 날 candidate they ticked anyway, which one that is.
    const showDay = days.length > 1;
    const c = row.candidate;
    const compare = c?.compare && !c.compare.missing ? c.compare : null;
    const notes = candidateNotes(c);
    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, opacity: row.include ? 1 : 0.55 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Checkbox checked={row.include} onChange={(e) => onChange({ include: e.target.checked })} sx={{ p: 0.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                    {row.raw_label || '화면 표시 없음'}
                </Typography>
                {showDay && (
                    <Select
                        size="small"
                        value={days.includes(row.list_date) ? row.list_date : ''}
                        onChange={(e) => onChange({ list_date: e.target.value })}
                        displayEmpty
                        error={Boolean(error) && error.startsWith('날짜')}
                        sx={{ flexShrink: 0, '& .MuiSelect-select': { fontSize: 16, py: 0.75 } }}
                    >
                        <MenuItem value=""><em>날짜</em></MenuItem>
                        {days.map((d) => <MenuItem key={d} value={d}>{formatListDate(d)}</MenuItem>)}
                    </Select>
                )}
            </Box>
            <TextField
                label="번호"
                value={row.phone_number}
                onChange={(e) => onChange({ phone_number: e.target.value })}
                placeholder="(201) 555-1234"
                fullWidth
                error={Boolean(error) && error.startsWith('번호')}
                inputProps={{ inputMode: 'tel' }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...INPUT_SX, mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    label="부재 시간"
                    type="time"
                    value={row.missed_time}
                    onChange={(e) => onChange({ missed_time: e.target.value })}
                    error={Boolean(error) && error.startsWith('시간')}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...INPUT_SX, flex: 1 }}
                />
                <TextField
                    label="횟수"
                    type="number"
                    value={row.missed_count}
                    onChange={(e) => onChange({ missed_count: e.target.value })}
                    inputProps={{ min: 1, max: 99, inputMode: 'numeric' }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...INPUT_SX, width: 96 }}
                />
            </Box>
            <DuplicateChoice candidate={c} value={row.on_duplicate} onChange={(v) => onChange({ on_duplicate: v })} fullWidth boxed timeUnknown={pastDay && !row.missed_time} />
            {(c?.matches || []).map((m) => (
                <Typography key={`${m.kind}-${m.id}`} variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {WHO_LABEL[m.contact_label] || ''} · {personName(m)}{m.consultation_number ? ` #${m.consultation_number}` : ''}
                </Typography>
            ))}
            {notes.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {notes.map((n) => (
                        <Chip key={n} size="small" label={n} variant="outlined" color={n.startsWith('확인') || n.startsWith('번호') ? 'warning' : 'default'} />
                    ))}
                </Box>
            )}
            {compare && (
                <Button
                    size="small"
                    fullWidth
                    sx={{ textTransform: 'none', mt: 0.5 }}
                    onClick={() => onChange({
                        phone_number: formatPhone(compare.phone_number),
                        missed_time: compare.missed_time || row.missed_time,
                        missed_count: compare.missed_count || row.missed_count,
                    })}
                >
                    두 번째 판독 사용: {formatPhone(compare.phone_number)} {compare.missed_time} ({compare.missed_count})
                </Button>
            )}
            {error && <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>{error}</Typography>}
        </Paper>
    );
};

/**
 * The phone page (docs/MISSED_CALLS_PLAN.md §6.5): the office iPhone's own screen for turning today's
 * 최근 통화 screenshots into rows — pick, upload, wait, review, 저장, all one thumb-width. It runs outside
 * the admin layout (no sidebar, no topbar, no socket), and shares its flow with the desktop dialog.
 */
const UploadFlow = ({ inGate = false }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [config, setConfig] = useState(null);
    const [saved, setSaved] = useState(null);      // what 저장 answered: { created, merged, rows }
    const [notToday, setNotToday] = useState(false);

    const goneRef = useRef(() => {});
    const flow = useScreenshotImport({ config, onGone: () => goneRef.current() });
    const { step, setStep, files, addFiles, removeFile, reset, progress, imp, review, setRow, addRow, busy, errors, maxFiles, maxMb } = flow;
    goneRef.current = ({ notified = false } = {}) => {
        if (!notified) enqueueSnackbar('이 업로드는 이미 저장되었거나 취소되었습니다', { variant: 'info' });
        reset();
    };

    useEffect(() => {
        missedCallsApi.config().then(setConfig).catch((err) => enqueueSnackbar(errorMessage(err), { variant: 'error' }));
    }, [enqueueSnackbar]);

    const pick = (e) => {
        const picked = Array.from(e.target.files || []);
        const limit = maxMb * 1024 * 1024;
        const ok = picked.filter((f) => f.type.startsWith('image/') && f.size <= limit);
        addFiles(ok, picked.length - ok.length);
        e.target.value = ''; // so the same photo can be picked again after taking it out
    };

    const cancel = async () => {
        await flow.discard();
        reset();
        setSaved(null);
        setNotToday(false);
    };

    const save = async () => {
        const out = await flow.save();
        if (out) setSaved(out);
    };

    // Numbers already on today's list that nobody has answered yet — 저장 waits for these.
    const undecided = review.filter((r) => r.include && r.candidate?.merge_into && !r.on_duplicate).length;
    const view = saved ? 'saved' : step;
    const today = formatListDate(config?.today);
    const dayWord = dayName(flow.importDate, config?.today);   // 오늘, or "9/14 (일)" for a past day's list
    const action = (label, onClick, { sx, ...props } = {}) => (
        <Button size="large" onClick={onClick} {...props} sx={{ ...BIG_BUTTON_SX, ...sx }}>{label}</Button>
    );

    return (
        <Box
            sx={{
                flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.default',
                ...(inGate
                    // The gate is already a 100dvh flex column with a header above us — fill what is left, or the
                    // pinned action bar (and its primary button) ends up a header's height below the viewport.
                    ? { minHeight: 0 }
                    : { height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' } }),
            }}
        >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>부재중 전화 업로드</Typography>
                    <Typography variant="caption" color="text.secondary">{today ? `${today} · ` : ''}iPhone 최근 통화 화면</Typography>
                </Box>
                {/* The list lives inside the admin layout, which sends anyone without a login to /signin — so
                    it is only offered to someone who actually has one. */}
                {!inGate && <Button size="small" sx={{ textTransform: 'none' }} onClick={() => navigate('/admin/missedCalls')}>목록</Button>}
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', px: 2, py: 2 }}>
                {view === 'confirm' && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        {config ? (
                            <>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>어느 날의 부재중을 올리나요?</Typography>
                                <DayChoice today={config.today} pastDays={config.past_days} value={flow.listDates} onChange={flow.setListDates} large multiple />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 3 }}>
                                    {dayUploadHint(flow.importDays, config.today)}
                                </Typography>
                                <Typography variant="h4" sx={{ mb: 1.5, fontWeight: 700 }}>
                                    이 스크린샷은 오늘{today ? ` ${today}` : ''} 찍은 건가요?
                                </Typography>
                                {notToday && <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>최근 통화 화면을 지금 다시 찍어서 올려 주세요.</Alert>}
                            </>
                        ) : <CircularProgress size={28} />}
                    </Box>
                )}

                {view === 'pick' && (
                    <>
                        <Button
                            component="label"
                            fullWidth
                            variant="outlined"
                            startIcon={<PhotoLibraryOutlinedIcon />}
                            sx={{ py: 3, fontSize: 16, textTransform: 'none', borderStyle: 'dashed', borderWidth: 2 }}
                        >
                            {files.length ? '사진 더 고르기' : '사진 선택'}
                            <input type="file" accept="image/*" multiple hidden onChange={pick} />
                        </Button>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                            최대 {maxFiles}장 · 장당 {maxMb}MB
                        </Typography>
                        {files.length > 0 && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1, mt: 2 }}>
                                {files.map((f, i) => (
                                    <Box key={f.url} sx={{ position: 'relative' }}>
                                        <img src={f.url} alt={`스크린샷 ${i + 1}`} style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                                        <IconButton
                                            size="small"
                                            onClick={() => removeFile(f.url)}
                                            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', p: 0.5 }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                )}

                {view === 'upload' && (
                    <Box sx={{ py: 4 }}>
                        <Typography sx={{ mb: 1 }}>올리는 중… {Math.round(progress * 100)}%</Typography>
                        <LinearProgress variant="determinate" value={Math.round(progress * 100)} />
                    </Box>
                )}

                {view === 'reading' && (
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                        <CircularProgress size={32} />
                        <Typography sx={{ mt: 1.5 }}>스크린샷을 읽는 중…</Typography>
                        <Typography variant="caption" color="text.secondary">보통 1분 안에 끝납니다. 이 화면을 열어 두세요.</Typography>
                    </Box>
                )}

                {view === 'failed' && <Alert severity="error">{imp?.error_message || '스크린샷을 읽지 못했습니다.'}</Alert>}

                {view === 'review' && (
                    <>
                        {flow.pastDay && (
                            <Alert severity="info" sx={{ mb: 1.5 }}>
                                {flow.multiDay
                                    ? `${flow.importDays.map(formatListDate).join(', ')} 목록에 나누어 저장합니다. 지난 날짜의 행은`
                                    : `${formatListDate(flow.importDate)} 목록에 저장합니다.`} 부재 시간을 비워 두면 "시간 모름"으로 저장됩니다.
                            </Alert>
                        )}
                        {(imp?.warnings || []).map((w) => (
                            <Alert key={`${w.reason}-${w.index ?? ''}`} severity={w.reason === 'compare_failed' ? 'info' : 'warning'} sx={{ mb: 1.5 }}>
                                {w.reason === 'not_call_history'
                                    ? `스크린샷 ${w.index + 1}은 최근 통화 화면이 아닌 것 같아 건너뛰었습니다.`
                                    : '두 번째 판독이 일부 실패해서 비교하지 않았습니다.'}
                            </Alert>
                        ))}
                        {review.map((r, i) => (
                            <ReviewCard
                                key={r.key}
                                days={flow.importDays}
                                row={r}
                                error={errors[i]}
                                onChange={(patch) => setRow(r.key, patch)}
                                pastDay={flow.pastDay}
                            />
                        ))}
                        {undecided > 0 && (
                            <Alert severity="warning" sx={{ mb: 1.5 }}>
                                {flow.multiDay ? '그 날 목록에 이미' : `${dayPhrase(flow.importDate, config?.today)} 이미`} 있는 번호가 {undecided}건 있습니다. 각 카드에서 합치기 / 새 행 / 무시를 고르면 저장할 수 있습니다.
                            </Alert>
                        )}
                        {review.length === 0 && (
                            <Typography color="text.secondary" sx={{ py: 2 }}>{flow.multiDay ? '고른 날짜의' : dayWord} 부재중 전화를 찾지 못했습니다. 빠진 통화가 있으면 행을 추가하세요.</Typography>
                        )}
                        <Button startIcon={<AddIcon />} fullWidth sx={{ textTransform: 'none' }} onClick={addRow}>행 추가</Button>
                    </>
                )}

                {view === 'saved' && (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>저장했습니다</Typography>
                        {flow.pastDay && (
                            <Typography sx={{ mt: 0.5 }}>{flow.importDays.map(formatListDate).join(', ')} 목록</Typography>
                        )}
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                            {(saved.created || []).length}개 추가 · {(saved.merged || []).length}개 갱신
                            {(saved.skipped || []).length > 0 && ` · ${saved.skipped.length}개 무시`}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    px: 2, pt: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper',
                    borderTop: 1, borderColor: 'divider',
                    pb: 'calc(12px + env(safe-area-inset-bottom))',
                }}
            >
                {view === 'confirm' && (
                    <>
                        {action('아니요', () => setNotToday(true), { variant: 'outlined' })}
                        {action('예, 오늘 찍었습니다', () => { setNotToday(false); setStep('pick'); }, { variant: 'contained', disabled: !config, sx: { flex: 2 } })}
                    </>
                )}
                {view === 'pick' && (
                    <>
                        {action('취소', cancel, { variant: 'outlined' })}
                        {action(`올리기 (${files.length})`, flow.upload, { variant: 'contained', disabled: !files.length, sx: { flex: 2 } })}
                    </>
                )}
                {view === 'upload' && action('올리는 중…', undefined, { variant: 'contained', disabled: true })}
                {view === 'reading' && action('취소', cancel, { variant: 'outlined' })}
                {view === 'failed' && (
                    <>
                        {action('취소', cancel, { variant: 'outlined', disabled: busy })}
                        {action('다시 시도', flow.retry, { variant: 'contained', disabled: busy, sx: { flex: 2 } })}
                    </>
                )}
                {view === 'review' && (
                    <>
                        {action('취소', cancel, { variant: 'outlined', disabled: busy })}
                        {action(
                            busy ? '저장 중…' : undecided ? `${undecided}건 선택 필요` : `저장 (${flow.included})`,
                            save,
                            { variant: 'contained', disabled: !flow.canSave, sx: { flex: 2 } },
                        )}
                    </>
                )}
                {view === 'saved' && (
                    <>
                        {!inGate && action('목록 보기', () => navigate('/admin/missedCalls'), { variant: 'outlined' })}
                        {action('더 올리기', () => { setSaved(null); reset(); }, { variant: 'contained', sx: { flex: 2 } })}
                    </>
                )}
            </Box>
        </Box>
    );
};

/**
 * No login (server docs/MISSED_CALLS_PLAN.md §6.10). The page lives outside the dashboard layout and lets
 * whoever holds the office iPhone in with one shared password, then asks which admin they are. An admin who
 * followed the desktop 업로드 link is already signed in and goes straight through on their own JWT.
 */
const MissedCallsUpload = () => (
    whoFromCookie() === 'admin'
        ? <UploadFlow />
        : <UploadGate><UploadFlow inGate /></UploadGate>
);

export default MissedCallsUpload;
